import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowPathIcon,
  PlusIcon,
  ChevronUpDownIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { adminApi } from '../services/api';

interface Topic {
  topic_id: number;
  name: string;
  position: number;
}

interface Chapter {
  chapter_id: number;
  name: string;
  subject_name?: string;
}

interface Subject {
  subject_id: number;
  name: string;
  chapters?: Chapter[];
}

// Sortable Topic Item Component
function SortableTopicItem({ topic, index, isDragging }: {
  topic: Topic;
  index: number;
  isDragging: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isItemDragging,
  } = useSortable({ id: topic.topic_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border ${
        isItemDragging ? 'opacity-50 border-indigo-500' : 'border-gray-200 dark:border-gray-700'
      } ${isDragging && !isItemDragging ? 'border-dashed' : ''}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            className="cursor-move p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            {...attributes}
            {...listeners}
          >
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
          </button>
          <span className="text-lg font-semibold text-gray-500 dark:text-gray-400">
            {topic.position}
          </span>
          <span className="text-gray-700 dark:text-gray-300">
            {topic.name}
          </span>
        </div>
        <span className="text-xs text-gray-400">
          ID: {topic.topic_id}
        </span>
      </div>
    </div>
  );
}

export default function TopicManager() {
  const [selectedSubject, setSelectedSubject] = useState<number | null>(null);
  const [selectedChapter, setSelectedChapter] = useState<number | null>(null);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [showAddTopic, setShowAddTopic] = useState(false);
  const [newTopicName, setNewTopicName] = useState('');
  const [newTopicPosition, setNewTopicPosition] = useState<number | undefined>();
  const [isDragging, setIsDragging] = useState(false);

  const queryClient = useQueryClient();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch subjects and chapters
  const { data: subjectsData, isLoading: loadingSubjects } = useQuery({
    queryKey: ['subjects-chapters'],
    queryFn: () => adminApi.getSubjectsAndChapters(),
  });

  // Fetch topics for selected chapter
  const { data: topicsData, isLoading: loadingTopics, refetch: refetchTopics } = useQuery({
    queryKey: ['topics-positions', selectedChapter],
    queryFn: () => adminApi.getTopicsWithPositions(selectedChapter!),
    enabled: !!selectedChapter,
  });

  // Update topics when data changes
  useEffect(() => {
    if (topicsData?.data?.topics) {
      setTopics(topicsData.data.topics);
      setHasChanges(false);
    }
  }, [topicsData]);

  // Mutations
  const reorderMutation = useMutation({
    mutationFn: ({ chapterId, topicIds }: { chapterId: number; topicIds: number[] }) =>
      adminApi.reorderTopics(chapterId, topicIds),
    onSuccess: () => {
      toast.success('Topics reordered successfully!');
      setHasChanges(false);
      refetchTopics();
    },
    onError: () => {
      toast.error('Failed to reorder topics');
    },
  });

  const resetMutation = useMutation({
    mutationFn: (chapterId: number) => adminApi.resetTopicOrder(chapterId),
    onSuccess: () => {
      toast.success('Topics reset to original order!');
      refetchTopics();
    },
    onError: () => {
      toast.error('Failed to reset topic order');
    },
  });

  const addTopicMutation = useMutation({
    mutationFn: (data: { name: string; chapter_id: number; position?: number }) =>
      adminApi.addTopicWithPosition(data),
    onSuccess: () => {
      toast.success('Topic added successfully!');
      setShowAddTopic(false);
      setNewTopicName('');
      setNewTopicPosition(undefined);
      refetchTopics();
    },
    onError: () => {
      toast.error('Failed to add topic');
    },
  });

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setIsDragging(false);
    const { active, over } = event;

    if (active.id !== over?.id) {
      setTopics((items) => {
        const oldIndex = items.findIndex((item) => item.topic_id === active.id);
        const newIndex = items.findIndex((item) => item.topic_id === over?.id);

        const newItems = arrayMove(items, oldIndex, newIndex);
        // Update positions
        return newItems.map((item, index) => ({
          ...item,
          position: index + 1,
        }));
      });
      setHasChanges(true);
    }
  };

  const handleSaveOrder = () => {
    if (!selectedChapter || !hasChanges) return;

    const topicIds = topics.map(t => t.topic_id);
    reorderMutation.mutate({ chapterId: selectedChapter, topicIds });
  };

  const handleReset = () => {
    if (!selectedChapter) return;
    resetMutation.mutate(selectedChapter);
  };

  const handleAddTopic = () => {
    if (!selectedChapter || !newTopicName) return;

    addTopicMutation.mutate({
      name: newTopicName,
      chapter_id: selectedChapter,
      position: newTopicPosition,
    });
  };

  const subjects = subjectsData?.data?.subjects || [];
  const chapters = subjectsData?.data?.chapters || [];
  const filteredChapters = selectedSubject
    ? chapters.filter((ch: any) => ch.subject_id === selectedSubject)
    : [];

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Topic Position Manager
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Drag and drop to reorder topics within chapters
        </p>
      </div>

      {/* Subject and Chapter Selectors */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Select Subject
          </label>
          <select
            value={selectedSubject || ''}
            onChange={(e) => {
              setSelectedSubject(e.target.value ? Number(e.target.value) : null);
              setSelectedChapter(null);
            }}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                     bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                     focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="">Select a subject...</option>
            {subjects.map((subject: Subject) => (
              <option key={subject.subject_id} value={subject.subject_id}>
                {subject.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Select Chapter
          </label>
          <select
            value={selectedChapter || ''}
            onChange={(e) => setSelectedChapter(e.target.value ? Number(e.target.value) : null)}
            disabled={!selectedSubject}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                     bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                     focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                     disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">Select a chapter...</option>
            {filteredChapters.map((chapter: any) => (
              <option key={chapter.chapter_id} value={chapter.chapter_id}>
                {chapter.chapter_name || chapter.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Action Buttons */}
      {selectedChapter && (
        <div className="flex flex-wrap gap-3 mb-6">
          <button
            onClick={handleSaveOrder}
            disabled={!hasChanges || reorderMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg
                     hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed
                     transition-colors"
          >
            {reorderMutation.isPending ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Saving...
              </>
            ) : (
              <>
                <CheckIcon className="h-4 w-4" />
                Save Order
              </>
            )}
          </button>

          <button
            onClick={handleReset}
            disabled={resetMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg
                     hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed
                     transition-colors"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Reset to Original
          </button>

          <button
            onClick={() => setShowAddTopic(!showAddTopic)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg
                     hover:bg-green-700 transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            Add Topic
          </button>

          {hasChanges && (
            <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <ExclamationTriangleIcon className="h-5 w-5" />
              <span>Unsaved changes</span>
            </div>
          )}
        </div>
      )}

      {/* Add Topic Form */}
      {showAddTopic && selectedChapter && (
        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg mb-6 border border-green-200 dark:border-green-800">
          <h3 className="text-lg font-semibold mb-4 text-green-800 dark:text-green-200">
            Add New Topic
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input
              type="text"
              value={newTopicName}
              onChange={(e) => setNewTopicName(e.target.value)}
              placeholder="Topic name"
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
            <input
              type="number"
              value={newTopicPosition || ''}
              onChange={(e) => setNewTopicPosition(e.target.value ? Number(e.target.value) : undefined)}
              placeholder="Position (optional)"
              min="1"
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
            <div className="flex gap-2">
              <button
                onClick={handleAddTopic}
                disabled={!newTopicName || addTopicMutation.isPending}
                className="px-4 py-2 bg-green-600 text-white rounded-lg
                         hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add
              </button>
              <button
                onClick={() => {
                  setShowAddTopic(false);
                  setNewTopicName('');
                  setNewTopicPosition(undefined);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Topics List */}
      {selectedChapter && (
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6">
          {loadingTopics ? (
            <div className="text-center py-8">
              <svg className="animate-spin h-8 w-8 mx-auto text-indigo-600" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Loading topics...</p>
            </div>
          ) : topics.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600 dark:text-gray-400">No topics found in this chapter</p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={topics.map(t => t.topic_id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {topics.map((topic, index) => (
                    <SortableTopicItem
                      key={topic.topic_id}
                      topic={topic}
                      index={index}
                      isDragging={isDragging}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}
    </div>
  );
}